import { useCallback, useEffect, useState } from 'react';
import { Sparkles, Shirt, Sword, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { listFeaturedAssets } from '../lib/db/assets';
import Mini3DViewer from './Mini3DViewer';
import type { GeneratedAsset } from '../lib/db/types';
import {
  filterAssetsWithValidPublicPreviews,
  type ValidatedFrontPageAsset,
} from '../utils/frontPagePreviewValidation';

interface Featured3DGallerySectionProps {
  compact?: boolean;
}

export default function Featured3DGallerySection({ compact = false }: Featured3DGallerySectionProps) {
  const [assets, setAssets] = useState<Array<ValidatedFrontPageAsset<GeneratedAsset>>>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const loadDemoAssetFallback = useCallback(async (): Promise<Array<ValidatedFrontPageAsset<GeneratedAsset>>> => {
    try {
      const response = await fetch('/assets/accessory/9f3a7c6c-851e-4e7c-b7d3-58c15ec8b64b/metadata.json');
      if (response.ok) {
        const metadata = await response.json();
        const asset: GeneratedAsset = {
          id: metadata.id,
          owner_id: '00000000-0000-0000-0000-000000000000',
          generator_type: metadata.type,
          name: metadata.name,
          description: metadata.description || null,
          is_listed: true,
          price_cents: 0,
          files: metadata.files,
          preview_files: {},
          user_fields: {},
          ai_result: {},
          created_at: metadata.created_at,
          updated_at: metadata.created_at,
        };
        return await filterAssetsWithValidPublicPreviews([asset], 1);
      }
    } catch {
      // Error handled silently
    }
    return [];
  }, []);

  const fetchFeaturedAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await listFeaturedAssets();

      if (error) {
        const fallback = await loadDemoAssetFallback();
        setAssets(fallback);
      } else if (!data || data.length === 0) {
        const fallback = await loadDemoAssetFallback();
        setAssets(fallback);
      } else {
        const validAssets = await filterAssetsWithValidPublicPreviews(data, data.length);
        if (validAssets.length === 0) {
          const fallback = await loadDemoAssetFallback();
          setAssets(fallback);
        } else {
          setAssets(validAssets);
        }
      }
    } catch {
      const fallback = await loadDemoAssetFallback();
      setAssets(fallback);
    } finally {
      setLoading(false);
    }
  }, [loadDemoAssetFallback]);

  useEffect(() => {
    fetchFeaturedAssets();
  }, [fetchFeaturedAssets]);

  function getTypeIcon(type: string) {
    switch (type) {
      case 'clothing':
        return <Shirt className="w-3 h-3" />;
      case 'accessory':
        return <Sword className="w-3 h-3" />;
      case 'pet':
        return <Heart className="w-3 h-3" />;
      default:
        return <Sparkles className="w-3 h-3" />;
    }
  }

  function getTypeBadgeColor(type: string) {
    switch (type) {
      case 'clothing':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accessory':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pet':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  }

  // Keep currentIndex valid when assets change
  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, Math.max(0, assets.length - 1)));
  }, [assets.length]);

  const showPrev = useCallback(() => {
    if (assets.length === 0) return;
    setCurrentIndex((i) => (i - 1 + assets.length) % assets.length);
  }, [assets.length]);

  const showNext = useCallback(() => {
    if (assets.length === 0) return;
    setCurrentIndex((i) => (i + 1) % assets.length);
  }, [assets.length]);

  // Keyboard navigation active only in compact mode
  useEffect(() => {
    if (!compact) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compact, showPrev, showNext]);

  if (loading) {
    if (compact) {
      return (
        <div className="bg-white rounded-xl shadow-2xl flex items-center justify-center border border-gray-100 h-full min-h-[400px]">
          <div className="text-center">
            <div className="animate-pulse text-gray-400">Loading featured assets...</div>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full py-16">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Featured Creations</h2>
            <p className="text-lg text-gray-600">See what users are making with ForgeBlocks</p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-gray-400">Loading featured assets...</div>
          </div>
        </div>
      </div>
    );
  }

  if (assets.length === 0) {
    if (compact) {
      return (
        <div className="bg-white rounded-xl shadow-2xl flex items-center justify-center border border-gray-100 h-full min-h-[400px]">
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <Heart className="w-16 h-16 text-orange-600" />
            </div>
            <p className="text-gray-500 font-medium">AI-Generated 3D Assets</p>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full py-16">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Featured Creations</h2>
            <p className="text-lg text-gray-600">See what users are making with ForgeBlocks</p>
          </div>
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Featured creations will appear here.</p>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    const current = assets[currentIndex] ?? assets[0];
    if (!current) {
      return (
        <div className="bg-white rounded-xl shadow-2xl flex items-center justify-center border border-gray-100 h-full min-h-[400px]">
          <div className="text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No featured assets available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 h-full min-h-[400px] flex flex-col items-center justify-center p-6 relative">
        <button
          aria-label="Previous featured"
          onClick={showPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md hover:bg-white"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>

        <Mini3DViewer
          glbUrl={current.glbUrl}
          resolvedGlbUrl={current.resolvedGlbUrl}
          assetId={current.asset.id}
          width={350}
          height={350}
        />

        <button
          aria-label="Next featured"
          onClick={showNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md hover:bg-white"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500 font-medium mb-1">Featured Creation</p>
          <h3 className="font-bold text-gray-900">{current.asset.name || 'Untitled Asset'}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-16">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-3">Featured Creations</h2>
          <p className="text-lg text-gray-600">See what users are making with ForgeBlocks</p>
        </div>

        <div className="relative">
          <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide snap-x snap-mandatory">
            {assets.map(({ asset, glbUrl, resolvedGlbUrl }) => {
              return (
                <div key={asset.id} className="flex-shrink-0 snap-center">
                  <div className="bg-white rounded-xl border-2 border-gray-100 hover:border-orange-200 hover:shadow-xl transition-all p-4">
                    <Mini3DViewer
                      glbUrl={glbUrl}
                      resolvedGlbUrl={resolvedGlbUrl}
                      assetId={asset.id}
                      width={250}
                      height={250}
                    />

                    <div className="mt-4">
                      <h3 className="font-bold text-gray-900 mb-2 truncate" style={{ maxWidth: '250px' }}>
                        {asset.name || 'Untitled Asset'}
                      </h3>

                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg border ${getTypeBadgeColor(
                          asset.generator_type
                        )}`}
                      >
                        {getTypeIcon(asset.generator_type)}
                        {asset.generator_type}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {assets.length > 4 && (
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          )}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
